import {Box, SelectChangeEvent, Stack, Typography} from '@mui/material';
import {FC, useContext, useEffect, useMemo, useState} from 'react';
import {PowerConsumptionProps} from './PowerConsumption.types';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,} from 'recharts';
import {chartWrapperSx, emptyStackSx, modeFieldSx, periodFieldSx, rootSx,} from './PowerConsumption.sx';
import {FormattedMessage, useIntl} from 'react-intl';
import {reportServiceApi} from 'src/api';
import {DateSelect} from '../DateSelect';
import {BoltOutlined} from '@mui/icons-material';
import {FilterBar} from '../FilterBar';
import {AMPFormSelect} from '../AMPFormSelect';
import {DatesType} from 'src/pages/Reports/Reports.types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import theme from 'src/theme';
import {getNumberFromPx} from '../../../utils/helper';
import DashboardLayoutContext from '../../../layouts/DashboardLayout/DashboardLayout.context';
import {emptyConsumptionPlaceholder} from '../../../utils/consts';
import {ConsumptionChartRequest} from 'src/types';

dayjs.extend(utc);

export const PowerConsumption: FC<PowerConsumptionProps> = ({
                                                                chargerId,
                                                                locationId,
                                                                userId,
                                                                companyId,
                                                            }) => {
    const [getChartData, transactions] = reportServiceApi.useLazyGetConsumptionChartQuery();
    const intl = useIntl();
    const responsive = useContext(DashboardLayoutContext);

    const [dates, setDates] = useState<DatesType>({
        allSessions: {
            from: dayjs.utc().startOf('year').valueOf(),
            to: dayjs.utc().valueOf(),
        },
    });
    const [displayMode, setDisplayMode] = useState<'consumption' | 'cost'>('consumption');

    const handleDisplayModeChange = (e: SelectChangeEvent) => {
        setDisplayMode(e.target.value as 'consumption' | 'cost');
    };

    const displayModeOptions = [
        {id: 0, value: 'consumption', name: intl.formatMessage({id: 'consumption'})},
        {id: 1, value: 'cost', name: intl.formatMessage({id: 'cost'})},
    ];

    const transactionsList = useMemo(() => {
        if (!transactions.data?.points) {
            return [];
        }
        const consumptionKey = intl.formatMessage({id: 'powerConsumption'});
        const costKey = intl.formatMessage({id: 'cost'});
        const filteredData = [...transactions.data.points]
            .filter((item) => Boolean(item.consumption))
            .sort((a, b) => a.timestamp - b.timestamp);

        if (filteredData.length === 0) {
            return [];
        }

        // The label follows the granularity the points were asked for, not how many came back: a year
        // of monthly points is twelve - fewer than 31 - and was being labelled as days, "01/11, 01/12".
        if (transactions.originalArgs?.granularity === 'MONTHLY') {
            // Keyed by the label, so two points of one month add up rather than repeat it.
            const grouped = new Map<string, { consumption: number; cost: number }>();
            filteredData.forEach((item) => {
                // Short month and year in the reader's language - "Jan 2026", "janv. 2026". In UTC, the
                // zone the report service cuts its months in: read in a zone west of it, every month
                // would carry the name of the one before.
                const month = intl.formatDate(item.timestamp, {month: 'short', year: 'numeric', timeZone: 'UTC'});
                const total = grouped.get(month) ?? {consumption: 0, cost: 0};
                total.consumption += item.consumption / 1000;
                total.cost += (item.cost || 0) / 10000;
                grouped.set(month, total);
            });

            return Array.from(grouped, ([month, total]) => ({
                name: month,
                [consumptionKey]: Number(total.consumption.toFixed(2)),
                [costKey]: Number(total.cost.toFixed(2)),
            }));
        }

        // Show individual days (single month)
        return filteredData.map((item) => ({
            name: dayjs(item.timestamp).format('DD/MM'),
            [consumptionKey]: Number((item.consumption / 1000).toFixed(2)),
            [costKey]: Number(((item.cost || 0) / 10000).toFixed(2)),
        }));
    }, [transactions, intl]);

    const title = intl.formatMessage({
        id: displayMode === 'consumption' ? 'powerConsumption' : 'cost',
    });

    const getData = async () => {
        const dateFrom = dates.allSessions.from!;
        const dateTo = dates.allSessions.to!;
        const diffDays = dayjs(dateTo).diff(dayjs(dateFrom), 'day');

        const data: ConsumptionChartRequest = {
            dateFrom,
            dateTo,
            granularity: diffDays > 31 ? 'MONTHLY' : 'DAILY',
        };

        if (chargerId) {
            data.chargerId = chargerId;
        }

        if (locationId) {
            data.locationId = locationId;
        }

        if (companyId) {
            data.companyId = companyId;
        }

        if (userId) {
            data.userId = userId;
        }

        await getChartData(data);
    };

    useEffect(() => {
        void getData();
    }, [dates]);

    useEffect(() => {
        // Re-enable the date selector after data changes (request completed)
        if (transactions.data !== undefined) {
            const timer = setTimeout(() => {
                setDisabled(false);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [transactions.data]);

    const handleChangeDates = (newFrom: number, newTo: number, field: string) => {
        return () => {
            setDisabled(true);
            setDates({
                [field]: {
                    from: newFrom,
                    to: newTo,
                },
            });
        };
    };
    const [disabled, setDisabled] = useState<boolean>(false);

    const handleAnimationEnd = () => {
        setDisabled(false);
    };

    const dataKey = intl.formatMessage({
        id: displayMode === 'consumption' ? 'powerConsumption' : 'cost',
    });
    const unit = displayMode === 'consumption' ? ` ${intl.formatMessage({id: 'kwh'})}` : ' CHF';
    const tooltipFormatter = (value: number) => [
        displayMode === 'consumption' ? `${value} kWh` : `${value} CHF`,
        dataKey,
    ];

    return (
        <Box sx={rootSx}>
            {/* Both controls sit on the heading's own line rather than in a band beneath it. A band
                is two tiers - what you are looking at, then what you do with it - and it earns its
                second row on a list of four hundred rows with six filters. This is one chart with two
                selects: a whole row of grey under the title to hold them was the emptiest part of the
                page. The row wraps on a narrow screen, so they drop under the title only when they
                genuinely do not fit. */}
            <FilterBar
                title={title}
                icon={<BoltOutlined/>}
                actions={(
                    <>
                        <Box sx={modeFieldSx}>
                            <AMPFormSelect
                                dense
                                data={displayModeOptions}
                                value={displayMode}
                                input="displayMode"
                                onChange={handleDisplayModeChange}
                            />
                        </Box>
                        <Box sx={periodFieldSx}>
                            <DateSelect
                                dense
                                handleTimeChange={handleChangeDates}
                                field={Object.keys(dates)[0]}
                                disabled={disabled}
                            />
                        </Box>
                    </>
                )}
            />
            <Box sx={chartWrapperSx}>
            {transactionsList.length > 0 ? (
                <ResponsiveContainer
                    width="100%"
                    height={responsive ? 350 : 400}
                >
                    <BarChart
                        data={transactionsList}
                        margin={{
                            top: getNumberFromPx(10),
                            right: getNumberFromPx(10),
                            left: getNumberFromPx(0),
                            bottom: getNumberFromPx(10),
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis
                            height={getNumberFromPx(60)}
                            fontSize={12}
                            fontWeight={600}
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            interval={transactionsList.length > 20 ? 'preserveStartEnd' : 0}
                        />
                        <YAxis
                            fontSize={12}
                            fontWeight={600}
                            unit={unit}
                        />
                        <Tooltip formatter={tooltipFormatter}/>
                        <Bar
                            onAnimationEnd={handleAnimationEnd}
                            maxBarSize={transactionsList.length > 15 ? getNumberFromPx(20) : getNumberFromPx(40)}
                            radius={[getNumberFromPx(10), getNumberFromPx(10), 0, 0]}
                            dataKey={dataKey}
                            fill={displayMode === 'consumption' ? theme.palette.primary.light : '#FFA559'}
                        />
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <Stack sx={emptyStackSx}>
                    <img
                        src={emptyConsumptionPlaceholder}
                        alt="empty"
                    />
                    <Typography>
                        <FormattedMessage id="emptyConsumption"/>
                    </Typography>
                </Stack>
            )}
            </Box>
        </Box>
    );
};
